import "../../styles/blocks/manual/manual.scss";

import React, {useEffect, useMemo, useRef, useState} from "react";
import ReactDOMServer from "react-dom/server";
import {Link, navigate} from "gatsby";
import {useLocation} from "@reach/router";
import PropTypes from "prop-types";

import NavigationTopLinks from "../../components/NavigationTopLinks";
import IconMenuDots from "../../icons/IconMenuDots";
import IconPlay from "../../icons/IconPlay";
import IconSearch from "../../icons/IconSearch";
import Modal from "../../components/Modal/Modal";
import NavigationButtons from "../../components/Manual/NavigationButtons";
import handleize from "../../helpers/handleize";
import {search} from "../../helpers/manualUtils";
import useHeaderHeight from "../../hooks/useHeaderHeight";
import ThemeManualNavigation from "./ThemeManualNavigation";

const youtubeRegex = /<a\s+(?:[^>]*?\s+)?href=["'](https?:\/\/www\.youtube\.com[^"']*)["'][^>]*>(.*?)<\/a>/gi;
const youtuDotBeRegex = /<a\s+(?:[^>]*?\s+)?href=["'](https?:\/\/youtu\.be[^"']*)["'][^>]*>(.*?)<\/a>/gi;
const playIconHtml = ReactDOMServer.renderToStaticMarkup(<IconPlay />);

const replaceYoutubeLinks = (match, href, content) => (
    `<a href="${href}" class="play" target="_blank" rel="noopener">${playIconHtml}${content}</a>`
);

const prepareHtml = (html = "") => html
    .replace(youtubeRegex, replaceYoutubeLinks)
    .replace(youtuDotBeRegex, replaceYoutubeLinks);

const getArticleId = (category, article, index) => (
    article.hash || handleize(
        `${category?.manualCategoryTitle || article.manualCategoryTitle || "manual"}-${article.blockTitle || index}`
    )
);

const getCategories = (manualCategory = []) => manualCategory.map((category, categoryIndex) => ({
    title: category.manualCategoryTitle,
    id: handleize(category.manualCategoryTitle || `category-${categoryIndex}`),
    articles: (category.manualCategoryBlocks || []).map((article, articleIndex) => ({
        id: getArticleId(category, article, articleIndex),
        title: article.blockTitle,
        content: prepareHtml(article.blockContent),
    })),
}));

const getSearchArticleId = (manualCategory, article, index) => {
    const category = manualCategory.find(({manualCategoryTitle, manualCategoryBlocks = []}) => (
        manualCategoryTitle === article.manualCategoryTitle
        && manualCategoryBlocks.some(block => (
            block.hash === article.hash
            || block.blockTitle === article.blockTitle
        ))
    ));

    return article.hash || getArticleId(category, article, index);
};

const ThemeManual = ({manualCategory, topLinks}) => {
    const location = useLocation();
    const sectionRef = useRef();
    const searchInputRef = useRef();
    const predictiveSearchRef = useRef();
    const [headerHeight] = useHeaderHeight();
    const [inputValue, setInputValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showPredictiveSearch, setShowPredictiveSearch] = useState(false);
    const [showSearchResult, setShowSearchResult] = useState(false);
    const [openSearchArticleIds, setOpenSearchArticleIds] = useState(() => new Set());
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const categories = useMemo(() => getCategories(manualCategory), [manualCategory]);
    const articles = useMemo(() => categories.flatMap(category => (
        category.articles.map(article => ({
            ...article,
            categoryTitle: category.title,
        }))
    )), [categories]);
    const [activeArticleId, setActiveArticleId] = useState(() => articles[0]?.id || "");

    const predictiveSearchResult = useMemo(() => {
        const query = inputValue.trim();

        return query ? search(manualCategory, query) : [];
    }, [inputValue, manualCategory]);

    const submittedSearchResult = useMemo(() => {
        const query = searchQuery.trim();

        return query ? search(manualCategory, query) : [];
    }, [searchQuery, manualCategory]);

    const selectArticle = articleId => {
        if (!articles.some(article => article.id === articleId)) {
            return;
        }

        setActiveArticleId(articleId);
        setShowPredictiveSearch(false);
        setShowSearchResult(false);
        setShowMobileSidebar(false);
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search).get("query")?.trim();

        if (!query) {
            return;
        }

        setInputValue(query);
        setSearchQuery(query);
        setShowSearchResult(true);
    }, []);

    useEffect(() => {
        if (location.hash) {
            selectArticle(location.hash.slice(1));
            setInputValue("");
            setSearchQuery("");

            if (location.search) {
                navigate(`${location.pathname}${location.hash}`, {replace: true});
            }
        }
    }, [location.hash]);

    useEffect(() => {
        const closePredictiveSearch = event => {
            if (searchInputRef.current?.contains(event.target)) {
                return;
            }

            if (predictiveSearchRef.current?.contains(event.target)) {
                return;
            }

            setShowPredictiveSearch(false);
        };

        document.addEventListener("mousedown", closePredictiveSearch);
        document.addEventListener("touchstart", closePredictiveSearch);

        return () => {
            document.removeEventListener("mousedown", closePredictiveSearch);
            document.removeEventListener("touchstart", closePredictiveSearch);
        };
    }, []);

    useEffect(() => {
        if (!location.hash || !sectionRef.current) {
            return;
        }

        const scrollTimeout = window.setTimeout(() => {
            window.scrollTo({
                top: sectionRef.current.offsetTop - headerHeight,
            });
        }, 50);

        return () => window.clearTimeout(scrollTimeout);
    }, [activeArticleId, headerHeight, location.hash]);

    const handleSearchSubmit = event => {
        event.preventDefault();

        const query = inputValue.trim();

        if (!query) {
            return;
        }

        navigate(`${location.pathname}?query=${encodeURIComponent(query)}`, {replace: true});
        setSearchQuery(query);
        setShowPredictiveSearch(false);
        setShowSearchResult(true);
        setOpenSearchArticleIds(new Set());
    };

    const toggleSearchArticle = articleId => {
        setOpenSearchArticleIds(currentIds => {
            const nextIds = new Set(currentIds);

            if (nextIds.has(articleId)) {
                nextIds.delete(articleId);
            } else {
                nextIds.add(articleId);
            }

            return nextIds;
        });
    };

    const navigation = (
        <ThemeManualNavigation
            categories={categories}
            activeArticleId={activeArticleId}
            onArticleSelect={selectArticle}
        />
    );
    const linklist = categories.map(category => ({
        title: category.title,
        links: category.articles.map(article => ({
            title: article.title,
            href: `#${article.id}`,
            isActive: article.id === activeArticleId,
        })),
    }));

    return (
        <section className="manual theme-manual visible" ref={sectionRef}>
            <div className="manual__container">
                <div className={`manual__sidebar ${showMobileSidebar ? "active" : ""}`}>
                    <NavigationTopLinks topLinks={topLinks} />

                    <div className="manual__navigation">
                        {navigation}
                    </div>
                </div>

                <div className="manual__main">
                    <form className="manual__form form" onSubmit={handleSearchSubmit}>
                        <label className="manual__form-input-group">
                            <IconSearch />

                            <input
                                type="text"
                                className="manual__form-input"
                                value={inputValue}
                                onChange={event => {
                                    setInputValue(event.target.value);
                                    setShowPredictiveSearch(true);
                                }}
                                onFocus={() => setShowPredictiveSearch(true)}
                                ref={searchInputRef}
                                placeholder="Search..."
                            />

                            {inputValue.trim() && showPredictiveSearch ? (
                                <ul className="manual__form-predictive-results" ref={predictiveSearchRef}>
                                    {predictiveSearchResult.length ? predictiveSearchResult.map((article, index) => {
                                        const articleId = getSearchArticleId(manualCategory, article, index);
                                        const content = `<b>${article.manualCategoryTitle} | </b> ${article.predictiveSearchTitle}`;

                                        return (
                                            <li className="manual__form-predictive-results-item" key={articleId}>
                                                <Link
                                                    className="manual__form-predictive-results-item-link"
                                                    to={`${location.pathname}#${articleId}`}
                                                    onClick={() => selectArticle(articleId)}
                                                    dangerouslySetInnerHTML={{__html: content}}
                                                    aria-label={`Link to article "${article.blockTitle}" in category "${article.manualCategoryTitle}"`}
                                                />
                                            </li>
                                        );
                                    }) : (
                                        <li className="manual__form-predictive-results--no-found">
                                            <p className="manual__search-no-found-heading">Not found.</p>
                                            <p className="manual__search-no-found-text">
                                                Try the name of the section or page you want to learn about.
                                            </p>
                                        </li>
                                    )}
                                </ul>
                            ) : null}
                        </label>
                    </form>

                    <button
                        className="manual__toggle-sidebar"
                        type="button"
                        onClick={() => setShowMobileSidebar(true)}
                    >
                        <IconMenuDots /> Sections
                    </button>

                    <Modal
                        isOpened={showMobileSidebar}
                        onClose={() => setShowMobileSidebar(false)}
                        isRounded={false}
                        isBordered={false}
                        isSpaced={false}
                        outerClassName="trap-focus--mobile-full"
                        className="manual__modal"
                        isCloseButtonShown={true}
                    >
                        <NavigationTopLinks topLinks={topLinks} />

                        <div className="manual__navigation">
                            {navigation}
                        </div>
                    </Modal>

                    <div className="manual__main-content theme-manual__content">
                        {showSearchResult && !submittedSearchResult.length ? (
                            <>
                                <p className="manual__search-no-found-heading">Not found.</p>
                                <p className="manual__search-no-found-text">
                                    Try the name of the section or page you want to learn about.
                                </p>
                            </>
                        ) : null}

                        {showSearchResult ? submittedSearchResult.map((article, index) => {
                            const articleId = getSearchArticleId(manualCategory, article, index);
                            const isOpen = openSearchArticleIds.has(articleId);

                            return (
                                <details
                                    className={`manual__panel ${isOpen ? "manual__panel--active" : ""}`}
                                    key={`search-${articleId}`}
                                    open={isOpen}
                                >
                                    <summary
                                        className="manual__button theme-manual__search-summary"
                                        onClick={event => {
                                            event.preventDefault();
                                            toggleSearchArticle(articleId);
                                        }}
                                    >
                                        <span>{article.manualCategoryTitle} | {article.blockTitle}</span>
                                    </summary>

                                    <div className="manual__inner">
                                        <div
                                            className="manual__content"
                                            dangerouslySetInnerHTML={{__html: prepareHtml(article.blockContent)}}
                                        />
                                    </div>
                                </details>
                            );
                        }) : null}

                        {articles.map(article => (
                            <details
                                className="theme-manual__article"
                                id={article.id}
                                key={article.id}
                                open={!showSearchResult && article.id === activeArticleId}
                            >
                                <summary className="theme-manual__article-summary" tabIndex="-1">
                                    {article.title}
                                </summary>

                                <div className="theme-manual__article-body">
                                    <h2 className="manual__list-title">
                                        {article.categoryTitle}
                                    </h2>

                                    <h3 className="medium-title manual__title">
                                        {article.title}
                                    </h3>

                                    <div
                                        className="rte manual__content"
                                        dangerouslySetInnerHTML={{__html: article.content}}
                                    />
                                </div>
                            </details>
                        ))}
                    </div>

                    <NavigationButtons linklist={linklist} />
                </div>
            </div>
        </section>
    );
};

ThemeManual.propTypes = {
    manualCategory: PropTypes.array.isRequired,
    topLinks: PropTypes.object.isRequired,
};

export default ThemeManual;
